<script lang="ts" setup generic="V = any">
import { computed, shallowRef } from 'vue';
import {
  IconCheckboxUnchecked,
  IconCheckboxChecked,
  IconCheckboxMixed,
  IconCheckboxSquare,
} from '@/icons';
import { hasSlotContent } from '../../utils';
import { areValuesEqual } from '../../../shared/utils';
import type { TrackBy } from '../../../shared/types';
type Props<Multiselect = boolean, ModelValue = Multiselect extends true ? V[] : V> = {
  ariaLabel?: string;
  disabled?: boolean;
  indeterminate?: boolean;
  multiselect?: boolean & Multiselect;
  value?: V;
  falseValue?: V;
  trackBy?: TrackBy<V>;
  modelValue?: ModelValue;
};
const {
  ariaLabel,
  disabled = false,
  indeterminate = false,
  multiselect = false,
  value = true as V,
  falseValue = false as V,
  trackBy,
} = defineProps<Props>();
defineEmits<{
  'update:modelValue': [value: Props['multiselect'] extends true ? V[] : V];
}>();
const modelValue = defineModel<V | V[]>();
const rootRef = shallowRef<HTMLLabelElement | null>(null);
const inputRef = shallowRef<HTMLInputElement | null>(null);
const checked = computed<boolean>(() => {
  if (typeof modelValue.value === 'undefined') {
    return false;
  }
  if (multiselect) {
    return Array.isArray(modelValue.value)
      ? modelValue.value.some((item) => areValuesEqual(item, value, trackBy))
      : false;
  }
  return !Array.isArray(modelValue.value) && areValuesEqual(modelValue.value, value, trackBy);
});
function onChange(event: Event): void {
  if (disabled) {
    return;
  }
  const { checked } = event.target as HTMLInputElement;
  if (multiselect) {
    if (!Array.isArray(modelValue.value)) {
      modelValue.value = [];
    }
    if (checked) {
      modelValue.value = [...modelValue.value, value];
    } else {
      modelValue.value = modelValue.value.filter((item) => !areValuesEqual(item, value, trackBy));
    }
  } else {
    modelValue.value = checked ? value : falseValue;
  }
}
</script>

<template>
  <label
    ref="rootRef"
    :class="[
      $style.root,
      {
        [$style.disabled]: disabled,
        [$style.checked]: checked,
        [$style.indeterminate]: indeterminate,
      },
    ]"
  >
    <input
      ref="inputRef"
      :aria-checked="indeterminate ? 'mixed' : checked"
      :aria-label="ariaLabel"
      :checked="checked"
      :disabled="disabled"
      type="checkbox"
      :class="$style.input"
      @change="onChange"
    />
    <span :class="$style.icon">
      <IconCheckboxMixed v-if="indeterminate" />
      <IconCheckboxChecked v-else-if="checked" />
      <IconCheckboxSquare v-else-if="disabled" />
      <IconCheckboxUnchecked v-else />
    </span>
    <span v-if="hasSlotContent($slots.default)" :class="$style.label">
      <slot />
    </span>
  </label>
</template>

<style lang="scss" module>
@use '../../styles/colors';
@use '../../styles/shared';
@use '../../styles/typography';

.root {
  position: relative;
  display: inline-flex;
  gap: 8px;
  vertical-align: middle;
  color: colors.$primary;
  font-family: typography.$font-family-primary;
  font-size: 13px;
  line-height: 16px;
  cursor: pointer;

  &.disabled {
    color: colors.$surface-40;
    cursor: not-allowed;
  }
}

.input {
  @include shared.sr-only();
}

.label {
  flex: 1 1 0%;
  min-width: 0;
}

.icon {
  width: 16px;
  height: 16px;
  flex: none;
  color: #d2d3d9;

  > svg {
    width: 100%;
    height: 100%;
  }
}

.root.indeterminate .icon,
.root.checked .icon {
  color: #010000;
}

.root.disabled .icon {
  color: #d2d3d9;
}
</style>
